import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiParam } from '../../shared/model/api.model';
import { Observable } from 'rxjs';
import { AdminContextService } from './admin-context.service';
import { GetFullURL, GetHeaders } from './api.util';
import { TokenService } from '@cudirect/shared-ui-library';

/**
 *
 * API Service
 * @export
 * @ class ApiService
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly tokenService = inject(TokenService);
  constructor(private readonly httpClient: HttpClient, private readonly adminContextService: AdminContextService) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mergeHeader(header: any): HttpHeaders {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preHeader = GetHeaders(this.adminContextService.adminPortalWidgetContext.apiConfig) as any;
    const headerObject = {};

    for (const key in preHeader) {
      if (preHeader.hasOwnProperty(key)) {
        const element = preHeader[key];
        headerObject[key] = element;
      }
    }

    for (const key in header) {
      if (header.hasOwnProperty(key)) {
        const element = header[key];
        if (element !== undefined) {
          headerObject[key] = element;
        } else if (headerObject.hasOwnProperty(key)) {
          delete headerObject[key];
        }
      }
    }

    const headers = new HttpHeaders(headerObject);
    return headers;
  }

  public requestApi<T>({ urlObj, body, token, queryParam, urlIds, callback, errorCallback }: ApiParam): void {
    this.request<T>({ urlObj, body, token, queryParam, urlIds }).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result: any) => {
        if (callback) {
          callback(result);
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (errorResponse: any) => {
        if (errorCallback) {
          errorCallback(errorResponse);
        }
      }
    );
  }

  public request<T>({ urlObj, body, token, queryParam, urlIds: urlId }: ApiParam): Observable<T> {
    const queryParams = queryParam || {};
    const suffix = this.adminContextService.apiConfig?.apis[urlObj.hostName]?.suffix || '';
    const url = GetFullURL(this.adminContextService.apiConfig?.domain, urlObj.url, suffix, urlId);
    urlObj.method = urlObj.method || 'GET';
    let params: HttpParams;

    if (Object.keys(queryParams).length > 0) {
      params = new HttpParams({ fromObject: queryParams });
    }

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }
    
    // NOTE: Do NOT rely on the Authorization header from widget/app context config.
    // That header is typically captured at initialization time and may become stale
    // after an auth/session refresh. Instead, fetching the latest access token at the
    // time of the request (from the token cache/service) and explicitly override
    // the request headers so each call uses the current token.
    const cachedToken = this.tokenService.getCachedToken();
    const authToken = cachedToken?.access_token;
    const authorizationHeaders: { [key: string]: string } = authToken
      ? { Authorization: `Bearer ${authToken}` }
      : {};

    const headers = this.mergeHeader({
      ...this.adminContextService.apiConfig.apis[urlObj.hostName]?.defaultHeaders,
      ...urlObj?.defaultHeaders,
      ...authorizationHeaders
    });

    return this.httpClient.request(urlObj.method, url, {
      body,
      headers,
      params
    }) as Observable<T>;
  }
}
